import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Clock, Tag, Pencil, Settings2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CatalogPlan,
  planTitle,
  formatDuration,
  getPlanListPrice,
  formatPriceAmount,
  asNum,
} from '@/client/types/promotion-pricing/catalog';
import { PlanFlags } from './plan-flags';
import { PlanFeatureList } from './plan-feature-list';
import { PlanParameters } from './plan-parameters';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';

interface PlanRowProps {
  plan: CatalogPlan;
  ccode: string;
  expanded: boolean;
  onToggle: () => void;
  onEditPrice: (plan: CatalogPlan) => void;
  onEditConfig: (plan: CatalogPlan) => void;
  onDelete: (plan: CatalogPlan) => void;
}

/** A single catalog row: summary line + framer-motion expandable detail. */
export const PlanRow = ({
  plan,
  ccode,
  expanded,
  onToggle,
  onEditPrice,
  onEditConfig,
  onDelete,
}: PlanRowProps) => {
  const { allowed: canManage, reason } = useGate(PERMISSIONS.promotion.edit);
  const tier = asNum(plan.tier);
  const price = getPlanListPrice(plan, ccode);

  return (
    <div className="border-b border-gray-50 last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onToggle())}
        className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-gray-50/70 sm:px-6"
      >
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200',
            expanded && 'rotate-180',
          )}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-bold text-gray-900">{planTitle(plan)}</p>
            {tier != null && (
              <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                Tier {tier}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDuration(plan)}
            </span>
            <span>toid {plan.toid}</span>
          </div>
        </div>

        <PlanFlags plan={plan} className="hidden max-w-[38%] justify-end md:flex" />

        <div className="flex shrink-0 items-center gap-3">
          <div className="text-right">
            {price ? (
              <span className="text-sm font-bold text-gray-900">
                {formatPriceAmount(Number(price.price), price.ccode ?? price.currency ?? ccode)}
              </span>
            ) : (
              <span className="text-xs italic text-gray-400">No price</span>
            )}
          </div>
          <button
            type="button"
            disabled={!canManage}
            title={canManage ? undefined : reason}
            onClick={(e) => {
              e.stopPropagation();
              if (canManage) onEditPrice(plan);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-blue-600 transition-colors hover:bg-blue-50 disabled:opacity-40"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{price ? 'Edit' : 'Set price'}</span>
          </button>
          <button
            type="button"
            disabled={!canManage}
            title={canManage ? 'Edit configuration' : reason}
            onClick={(e) => {
              e.stopPropagation();
              if (canManage) onEditConfig(plan);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
          >
            <Settings2 className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Config</span>
          </button>
          <button
            type="button"
            disabled={!canManage}
            title={canManage ? 'Delete plan' : reason}
            onClick={(e) => {
              e.stopPropagation();
              if (canManage) onDelete(plan);
            }}
            className="inline-flex items-center rounded-lg px-2 py-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="space-y-4 bg-gray-50/50 px-4 pb-5 pt-1 sm:px-14">
              <PlanFlags plan={plan} className="md:hidden" />

              {plan.description && plan.description !== 'None' && (
                <p className="text-sm text-gray-500">{plan.description}</p>
              )}

              <PlanFeatureList plan={plan} />

              <div className="rounded-lg border border-gray-100 bg-white p-3">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <Tag className="h-3 w-3" />
                  Configuration
                </p>
                <PlanParameters plan={plan} />
                {!Object.keys(plan.parameters && plan.parameters !== 'None' ? plan.parameters : {})
                  .length && <p className="text-sm text-gray-400">No parameters configured.</p>}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
